import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import { UPDataGrid } from 'components/data-grid';
import DeleteModal from 'components/delete-modal';
import { useGridSearch } from 'hooks/useGridSearch';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store';
import { deleteCostCentersAction, getCostCentersAction } from 'store/actions/company-cost-centers-actions';
import { getLocationsDropdownItemsAction } from 'store/actions/company-locations-actions';
import { getPayrollAction } from 'store/actions/payroll-actions';
import { costCentersSelector } from 'store/selectors/company-cost-centers-selectors';
import { locationsDropdownItemsSelector } from 'store/selectors/company-locations-selectors';
import IPayrollCostCenter from 'types/company/payroll/IPayrollCostCenter';
import { EditPayrollCostCenter } from '.';
import {
  COMPANY_INFORMATION__BUS_NUMBER,
  COMPANY_INFORMATION__CITY,
  COMPANY_INFORMATION__POSTAL_CODE,
  COMPANY_INFORMATION__STREET_NAME,
  COMPANY_INFORMATION__STREET_NUMBER,
  COMPANY_PAYROLL__COST_CENTER_ADD_NEW_COST_CENTER,
  COMPANY_PAYROLL__COST_CENTER_DESCRIPTION,
  COMPANY_PAYROLL__COST_CENTER_EDIT_COST_CENTER,
  COMPANY_PAYROLL__COST_CENTER_LATITUDE,
  COMPANY_PAYROLL__COST_CENTER_LONGITUDE,
  CONTRACTS__COMPANY_LOCATION,
  GENERAL__DELETE_MESSAGE,
  GENERAL__DELETE_SELECTED,
  GENERAL__EDIT,
} from '../../../translations/constants';
import { PayrollTabActionButton, PayrollTabContainer } from './styles';

const PayrollCostCenters = (): JSX.Element => {
  const [t] = useTranslation();
  const dispatch = useAppDispatch();
  const { companyId } = useParams();

  const costCenters = useAppSelector(costCentersSelector);
  const locationsDropdownItems = useAppSelector(locationsDropdownItemsSelector);

  const [rowOnHover, setRowOnHover] = useState<number>();
  const [openAddEditCostCenter, setOpenAddEditCostCenter] = useState<boolean>(false);
  const [isAddNewButtonClicked, setIsAddNewButtonClicked] = useState<boolean>(false);

  useEffect(() => {
    dispatch(getCostCentersAction(toNumber(companyId)));
    dispatch(getLocationsDropdownItemsAction({ companyId: toNumber(companyId) }));
    dispatch(getPayrollAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const onOpenAddEditModal = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    setOpenAddEditCostCenter(true);
  }, []);

  const onCloseAddEditModal = useCallback(() => {
    setOpenAddEditCostCenter(false);
    setIsAddNewButtonClicked(false);
    setTimeout(() => {
      dispatch(getCostCentersAction(toNumber(companyId)));
    }, 1500);
  }, [companyId, dispatch]);

  const getCostCenterOnEdit = useCallback(() => {
    return costCenters.find(costCenter => costCenter.id === rowOnHover);
  }, [costCenters, rowOnHover]);

  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const onOpenDeleteModal = useCallback(event => {
    event.preventDefault();
    event.stopPropagation();
    setOpenDeleteModal(true);
  }, []);

  const onSelectionChange = useCallback((ids: Iterable<number>) => {
    const selectedIDs = Array.from(ids);
    setSelectedRows(selectedIDs);
  }, []);

  const matches = useCallback(
    (row: IPayrollCostCenter, term: string) => {
      const needle = term.toLowerCase();
      const denormalizedLocationName = row.locationName ?? '';
      const resolvedLocationName =
        locationsDropdownItems?.find(l => l.value?.toString() === row.locationId?.toString())?.label ?? '';
      return (
        (row.name ?? '').toLowerCase().includes(needle) ||
        (row.streetName ?? '').toLowerCase().includes(needle) ||
        (row.streetNumber ?? '').toLowerCase().includes(needle) ||
        (row.busNumber ?? '').toLowerCase().includes(needle) ||
        (row.city ?? '').toLowerCase().includes(needle) ||
        (row.postalCode ?? '').toLowerCase().includes(needle) ||
        String(row.latitude ?? '').includes(needle) ||
        String(row.longitude ?? '').includes(needle) ||
        denormalizedLocationName.toLowerCase().includes(needle) ||
        resolvedLocationName.toLowerCase().includes(needle)
      );
    },
    [locationsDropdownItems],
  );

  const { onSearch, filteredRows } = useGridSearch(costCenters ?? [], matches);

  const columns: GridColDef[] = [
    { field: 'id', headerName: '', hide: true },
    { field: 'name', headerName: t(COMPANY_PAYROLL__COST_CENTER_DESCRIPTION), width: 200 },
    { field: 'locationName', headerName: t(CONTRACTS__COMPANY_LOCATION), width: 200 },
    { field: 'streetName', headerName: t(COMPANY_INFORMATION__STREET_NAME), width: 200 },
    { field: 'streetNumber', headerName: t(COMPANY_INFORMATION__STREET_NUMBER), width: 200 },
    { field: 'busNumber', headerName: t(COMPANY_INFORMATION__BUS_NUMBER), width: 200 },
    { field: 'city', headerName: t(COMPANY_INFORMATION__CITY), width: 200 },
    { field: 'postalCode', headerName: t(COMPANY_INFORMATION__POSTAL_CODE), width: 200 },
    { field: 'latitude', headerName: t(COMPANY_PAYROLL__COST_CENTER_LATITUDE), width: 150 },
    { field: 'longitude', headerName: t(COMPANY_PAYROLL__COST_CENTER_LONGITUDE), width: 150 },
    {
      field: 'actions',
      headerName: '',
      align: 'right',
      width: 100,
      sortable: false,
      disableColumnMenu: true,
      renderCell: params => {
        if (rowOnHover === params.id) {
          return <UPButton onClick={onOpenAddEditModal} text={t(GENERAL__EDIT)} outlined />;
        }
        return null;
      },
    },
  ];

  return (
    <PayrollTabContainer>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <PayrollTabActionButton
          text={`+ ${t(COMPANY_PAYROLL__COST_CENTER_ADD_NEW_COST_CENTER)}`}
          onClick={event => {
            setRowOnHover(null);
            setIsAddNewButtonClicked(true);
            onOpenAddEditModal(event);
          }}
        />
        <Box sx={{ marginLeft: '15px' }}>
          <PayrollTabActionButton
            text={t(GENERAL__DELETE_SELECTED)}
            onClick={onOpenDeleteModal}
            outlined
            disabled={!(selectedRows.length > 0)}
          />
        </Box>
        <Box display="flex" marginLeft={2} alignItems="center">
          <CustomSearchBox onSearch={onSearch} />
        </Box>
      </Box>
      <Box sx={{ height: '400px', width: '100%' }}>
        <UPDataGrid
          rows={filteredRows}
          columns={columns}
          setRowOnHover={setRowOnHover}
          onSelectionModelChange={onSelectionChange}
          getRowId={r => r.id}
        />
        {openAddEditCostCenter && (
          <EditPayrollCostCenter
            open={openAddEditCostCenter}
            onClose={onCloseAddEditModal}
            costCenter={getCostCenterOnEdit()}
            title={
              isAddNewButtonClicked
                ? t(COMPANY_PAYROLL__COST_CENTER_ADD_NEW_COST_CENTER)
                : t(COMPANY_PAYROLL__COST_CENTER_EDIT_COST_CENTER)
            }
            isEdit={!isAddNewButtonClicked}
          />
        )}
        {openDeleteModal && (
          <DeleteModal
            content={t(GENERAL__DELETE_MESSAGE)}
            open={openDeleteModal}
            onClose={() => setOpenDeleteModal(false)}
            onDelete={() => {
              dispatch(deleteCostCentersAction({ companyId, costCentresIds: selectedRows }));
            }}
          />
        )}
      </Box>
    </PayrollTabContainer>
  );
};

export default PayrollCostCenters;
